import { useMemo, type ReactNode } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { FilterPill, type FilterBarOverflow } from '@/components/filter-bar';
import { WorkspacesIcon } from '@/components/layout/icons';
import { useListParams } from '@/lib/list-view/use-list-params';

export const WORKSPACE_PARAM = 'workspace';
export const WORKSPACE_PILL_LIMIT = 5;

export type WorkspaceFilterRow = { readonly workspaceId: string; readonly workspaceName: string | null };

export type WorkspaceFilter = {
  /** The chosen workspace, or `null` — also `null` when the address names one that is not in `rows`. */
  readonly workspaceId: string | null;
  readonly setWorkspace: (workspaceId: string | null) => void;
  /** The pills, or `null` when fewer than two workspaces are present. */
  readonly pills: ReactNode | null;
  /** Always defined; `items` is empty when there are no pills. */
  readonly overflow: FilterBarOverflow;
};

type PresentWorkspace = { readonly id: string; readonly name: string; readonly count: number };

export function useWorkspaceFilter(
  rows: ReadonlyArray<WorkspaceFilterRow>,
  options: {
    readonly clearWith?: ReadonlyArray<string>;
    readonly labels?: { readonly label: string; readonly placeholder: string };
  } = {},
): WorkspaceFilter {
  const tFilters = useTranslations('pages.admin.shell.filters');
  const { params, setParams } = useListParams();
  const { clearWith = [], labels } = options;

  // Ordered request count desc, then name asc: the busiest workspaces stay pills and
  // the order does not reshuffle as the backend's row order changes.
  const present = useMemo<ReadonlyArray<PresentWorkspace>>(() => {
    const entries = new Map<string, { name: string; count: number }>();

    for (const row of rows) {
      const entry = entries.get(row.workspaceId);

      if (entry === undefined) {
        entries.set(row.workspaceId, { name: row.workspaceName ?? row.workspaceId, count: 1 });
      } else {
        entry.count += 1;

        // A row without a name must not decide the label: the first real name wins over the id fallback.
        if (entry.name === row.workspaceId && row.workspaceName !== null) entry.name = row.workspaceName;
      }
    }

    return [...entries.entries()]
      .map(([id, { name, count }]) => ({ id, name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'de'));
  }, [rows]);

  // Only a workspace present in the rows filters them: a stale link would otherwise
  // narrow the list to nothing with no active pill on screen to clear it.
  const requested = params.get(WORKSPACE_PARAM);
  const workspaceId = present.some((workspace) => workspace.id === requested) ? requested : null;

  const setWorkspace = (next: string | null) =>
    setParams({ [WORKSPACE_PARAM]: next, ...Object.fromEntries(clearWith.map((key) => [key, null])) });

  // The ACTIVE workspace leads the group, because FilterBar's two-row cap clips whatever
  // renders LAST and the active pill is the only control that names and clears the filter.
  const visible = present.slice(0, WORKSPACE_PILL_LIMIT);
  const active = present.find((workspace) => workspace.id === workspaceId);
  const pillWorkspaces =
    active === undefined
      ? visible
      : [active, ...visible.filter((workspace) => workspace.id !== active.id).slice(0, WORKSPACE_PILL_LIMIT - 1)];
  const hasPills = present.length >= 2;

  const pills = hasPills ? (
    <>
      {pillWorkspaces.map((workspace) => (
        <FilterPill
          key={workspace.id}
          icon={WorkspacesIcon}
          label={workspace.name}
          active={workspaceId === workspace.id}
          onToggle={() => setWorkspace(workspaceId === workspace.id ? null : workspace.id)}
        />
      ))}
    </>
  ) : null;

  // The outlet reaches every present workspace but the active one, so a pill the
  // cap clips stays selectable.
  const overflow: FilterBarOverflow = {
    label: labels?.label ?? tFilters('more-workspaces'),
    placeholder: labels?.placeholder ?? tFilters('more-workspaces-placeholder'),
    items: hasPills
      ? present.filter((workspace) => workspace.id !== workspaceId).map(({ id, name }) => ({ id, label: name }))
      : [],
    onSelect: setWorkspace,
  };

  return { workspaceId, setWorkspace, pills, overflow };
}
